/*
 * CAPTURE-OWNED MODULE
 * Destinations settings page for capture-ui
 * CRUD for publish destinations with test connection support.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { captureAPI } from '../services/api';
import FieldMappingEditor, { withoutIdentityEntries } from '../components/FieldMappingEditor';
import { useIsAdmin } from '../identity/useIsAdmin';
import CloudFolderPickerDialog, { connectionSource } from '../components/CloudFolderPickerDialog';
import { isConnectionUsable } from '../constants/cloudChannels';
import './DestinationsPage.css';

function withoutLibraryBaseUrl(config) {
  const { library_base_url: _ignored, ...rest } = config || {};
  return rest;
}

// What happens when a file of the same name is already in the folder. The
// values are the server's (rename / overwrite / fail); 'rename' is its default.
export const DUPLICATE_POLICY_OPTIONS = [
  { value: 'rename', label: 'Keep both: add a number, as in "name (1).pdf"' },
  { value: 'overwrite', label: 'Replace the existing file' },
  { value: 'fail', label: 'Do not publish: fail, so someone can look' },
];

// The optional file of extracted fields saved beside the document.
export const SIDECAR_FORMAT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'json', label: 'JSON' },
  { value: 'csv', label: 'CSV' },
  { value: 'xml', label: 'XML' },
  { value: 'txt', label: 'TXT' },
];

// Connector type metadata. A config field may be a text input, a `select`, or
// the OneDrive folder chooser; a text field marked `showFieldChips` gets the
// "insert field" chips, and the `filename_template` key gets the live preview.
// `defaults` is the config a new destination of the type starts from.
// `needsConnection` types are offered only when Microsoft 365 is connected.
const CONNECTOR_TYPES = {
  document_library: {
    label: 'Document Library',
    icon: '📚',
    defaults: { default_folder: '' },
    configFields: [
      { key: 'default_folder', label: 'Default Folder', type: 'text', placeholder: '/Captured Documents' },
    ],
  },
  onedrive: {
    label: 'OneDrive',
    icon: '☁️',
    needsConnection: true,
    defaults: { duplicate_policy: 'rename', sidecar_format: 'none' },
    configFields: [
      { key: 'folder_item_id', label: 'Folder', type: 'onedrive_folder', required: true },
      {
        key: 'subfolder_template', label: 'Subfolder rule', type: 'text', showFieldChips: true,
        placeholder: '{document_type}/{vendor_name}',
        helperText: 'Optional. Folders inside the chosen folder, one level per "/". A field with no value becomes "Unknown".',
      },
      {
        key: 'filename_template', label: 'Filename rule', type: 'text', showFieldChips: true,
        placeholder: '{vendor_name} {invoice_number}',
        helperText: "Optional. Leave empty to keep the document's own name. If any field in the rule has no value, the document keeps its own name.",
      },
      {
        key: 'duplicate_policy', label: 'If a file with the same name is already there', type: 'select',
        options: DUPLICATE_POLICY_OPTIONS, defaultValue: 'rename',
      },
      {
        key: 'sidecar_format', label: 'Metadata file', type: 'select',
        options: SIDECAR_FORMAT_OPTIONS, defaultValue: 'none',
        helperText: 'A file of the extracted fields and vendor values, saved beside the document under the same name.',
      },
    ],
  },
};

/** Refusals of a OneDrive destination that choosing another folder can put right. */
const FOLDER_REFUSALS = new Set([
  'destination_folder_watched',
  'destination_folder_gone',
  'destination_drive_mismatch',
  'destination_folder_is_root',
  'destination_not_a_folder',
  'destination_folder_too_deep',
  'destination_folder_required',
]);
/** ...and those that connecting Microsoft 365 puts right. */
const CONNECTION_REFUSALS = new Set(['connection_required', 'connection_reconnect_needed']);
const SOURCES_PAGE = '/settings/ingestion';

/** The server's refusal as the page shows it: its own text, its code, its reference. */
function refusalOf(err, fallback) {
  const body = err?.response?.data;
  // The code is for whoever is debugging: the console, and a data attribute on
  // the notice. A person is shown the sentence.
  if (body?.code) console.warn('[destinations] request refused', { status: err?.response?.status, code: body.code });
  return {
    error: body?.error || err?.message || fallback,
    code: body?.code || null,
    errorId: body?.error_id || null,
  };
}

// Sample data for filename preview
const PREVIEW_SAMPLE = {
  vendor_name: 'Office_Depot',
  invoice_number: 'INV-2025-100003',
  invoice_date: '2025-01-03',
  vendor_display_name: 'Office Depot Inc',
  vendor_number: 'V-1001',
  document_type: 'Invoice',
};

// Export tokens that come from the document's vendor record snapshot. They get
// their own chip row, shown only when the server lists them in exportTokens.
const VENDOR_TOKENS = ['vendor_display_name', 'vendor_number'];

/** The note for a rule that uses a vendor token, by the rule's config key. */
const VENDOR_NOTES = {
  subfolder_template: 'A document with no vendor value (no vendor record, no value on the record, or the value was cleared) goes in an Unknown folder.',
  filename_template: 'A document with no vendor value (no vendor record, no value on the record, or the value was cleared) keeps its own name.',
};

/** Does the rule text name a vendor token? */
function usesVendorToken(template) {
  if (typeof template !== 'string') return false;
  return VENDOR_TOKENS.some(token => template.includes(`{${token}}`));
}

function resolveTokens(template, fields) {
  if (!template) return '';
  return template.replace(/\{([^}]+)\}/g, (match, key) => {
    const value = fields[key];
    return (value !== undefined && value !== null && value !== '') ? String(value) : match;
  });
}

const DestinationsPage = () => {
  const isAdmin = useIsAdmin();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    // No type until one is chosen (ruled 2026-10-10): the list opens on
    // "Choose a type..." and Create stays disabled until it is.
    connector_type: '',
    description: '',
    config: {},
    is_default: false,
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // The tenant's Microsoft 365 connection: undefined while it is being read,
  // null when there is none (or it could not be read). OneDrive is offered
  // only with a usable one.
  const [connection, setConnection] = useState(undefined);
  // The folder picker: closed, open for the form, or open to put right a
  // destination whose reactivation was refused.
  const [picker, setPicker] = useState(null);
  // Why a reactivation was refused, per destination.
  const [reactivateRefusals, setReactivateRefusals] = useState({});
  // The delete confirmation: closed, or open for one destination. `preview` is
  // null while the workflows that use it are being read; Delete stays disabled
  // until they have been, so nobody confirms a delete without having been told.
  const [deleting, setDeleting] = useState(null);

  // Test connection state per destination
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  // Field key chips for filename/subfolder template fields
  const [fieldKeys, setFieldKeys] = useState([]);
  // Document-row tokens the server lists beside fieldKeys (none from an older server)
  const [exportTokens, setExportTokens] = useState([]);
  const filenameInputRef = useRef(null);
  const subfolderInputRef = useRef(null);

  const loadDestinations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await captureAPI.destinations.list({ includeInactive: true });
      if (res.ok) {
        setDestinations(res.destinations || []);
      } else {
        throw new Error(res.error || 'Failed to load destinations');
      }
    } catch (err) {
      setError(err.message || 'Failed to load destinations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDestinations();
  }, [loadDestinations]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await captureAPI.ingestion.getConnection();
        if (live) setConnection(res?.connection ?? null);
      } catch {
        // Not readable is not connected, as far as offering OneDrive goes.
        if (live) setConnection(null);
      }
    })();
    return () => { live = false; };
  }, []);

  const m365Usable = isConnectionUsable(connection);
  /** May a NEW destination of this type be started now? */
  const typeAvailable = (type) => !CONNECTOR_TYPES[type]?.needsConnection || m365Usable;

  // Auto-dismiss success messages
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // Load field keys when the form opens for a type with a template field
  useEffect(() => {
    if (!showForm) return;
    const fields = CONNECTOR_TYPES[formData.connector_type]?.configFields || [];
    if (!fields.some(f => f.showFieldChips)) return;
    captureAPI.destinations.getFieldKeys()
      .then(res => {
        if (!res.ok) return;
        setFieldKeys(res.fieldKeys || []);
        setExportTokens(Array.isArray(res.exportTokens) ? res.exportTokens.filter(t => typeof t === 'string') : []);
      })
      .catch(() => {});
  }, [showForm, formData.connector_type]);

  // The vendor row holds the vendor tokens the server listed. Every other export
  // token joins the "Insert field:" row after the extracted fields, once.
  const vendorChips = VENDOR_TOKENS.filter(token => exportTokens.includes(token));
  const fieldChips = [...new Set([...fieldKeys, ...exportTokens])].filter(key => !vendorChips.includes(key));

  const resetForm = () => {
    setFormData({
      name: '',
      connector_type: '',
      description: '',
      config: {},
      is_default: false,
    });
    setEditingId(null);
    setFormError(null);
  };

  /** Another type is another form: its config starts from that type's defaults. */
  const handleTypeChange = (type) => {
    setFormData(prev => ({ ...prev, connector_type: type, config: { ...(CONNECTOR_TYPES[type]?.defaults || {}) } }));
    setFormError(null);
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (dest) => {
    setFormData({
      name: dest.name,
      connector_type: dest.connector_type,
      description: dest.description || '',
      // library_base_url is no longer honoured (the server publishes to its own
      // configured Library and drops the key on save); do not send it back.
      config: withoutLibraryBaseUrl(dest.config),
      is_default: dest.is_default,
    });
    setEditingId(dest.id);
    setShowForm(true);
    setFormError(null);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleConfigChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [key]: value },
    }));
  };

  const handleSave = async () => {
    if (!editingId && !formData.connector_type) {
      // Create is disabled until a type is chosen; this is the same rule for
      // any other way of getting here.
      setFormError({ error: 'Choose a type for this destination.' });
      return;
    }
    if (!formData.name.trim()) {
      setFormError({ error: 'Name is required' });
      return;
    }
    const required = (CONNECTOR_TYPES[formData.connector_type]?.configFields || []).filter(f => f.required);
    const missing = required.find(f => !formData.config?.[f.key]);
    if (missing) {
      setFormError({
        error: missing.type === 'onedrive_folder' ? 'Choose a OneDrive folder for this destination.' : `${missing.label} is required`,
      });
      return;
    }
    setSaving(true);
    setFormError(null);
    // A rename to the same name renames nothing; it is not submitted. A Library
    // destination has no mapping of its own (the workflow Publish node renames),
    // so a stored one is not sent back.
    let payload = formData;
    if (formData.connector_type === 'document_library' && formData.config?.field_mapping) {
      const { field_mapping: _dropped, ...rest } = formData.config;
      payload = { ...formData, config: rest };
    } else if (formData.config?.field_mapping) {
      payload = { ...formData, config: { ...formData.config, field_mapping: withoutIdentityEntries(formData.config.field_mapping) } };
    }
    if (formData.connector_type === 'onedrive') {
      // An empty rule is no rule: it is not sent as ''.
      const config = { ...payload.config };
      for (const key of ['subfolder_template', 'filename_template']) {
        if (typeof config[key] === 'string') config[key] = config[key].trim();
        if (!config[key]) delete config[key];
      }
      payload = { ...payload, config };
    }
    try {
      if (editingId) {
        const res = await captureAPI.destinations.update(editingId, payload);
        if (!res.ok) throw new Error(res.error);
        setSuccessMsg('Destination updated');
      } else {
        const res = await captureAPI.destinations.create(payload);
        if (!res.ok) throw new Error(res.error);
        setSuccessMsg('Destination created');
      }
      closeForm();
      loadDestinations();
    } catch (err) {
      // The server's refusal carries its reason (two fields renamed to one
      // target; a folder a source watches; Microsoft 365 not connected). It is
      // shown as sent, in the form, with its code and reference.
      setFormError(refusalOf(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this destination? It can be re-enabled later.')) return;
    try {
      const res = await captureAPI.destinations.delete(id);
      if (!res.ok) throw new Error(res.error);
      setSuccessMsg('Destination deactivated');
      loadDestinations();
    } catch (err) {
      setError(err.message);
    }
  };

  const clearRefusal = (id) => setReactivateRefusals(prev => {
    const { [id]: _gone, ...rest } = prev;
    return rest;
  });

  /**
   * Reactivate. The server checks the destination again first (for OneDrive:
   * the folder, against the Microsoft 365 connection as it is now, and against
   * the folders sources watch) and may refuse. A refusal is shown ON THE CARD,
   * in the server's words, and stays until it is put right.
   *
   * `config` is sent only from "Choose folder": the same request then replaces
   * the folder and reactivates, and the server validates the new folder.
   */
  const handleReactivate = async (id, config) => {
    try {
      const res = await captureAPI.destinations.update(id, config ? { config, is_active: true } : { is_active: true });
      if (!res.ok) throw new Error(res.error);
      clearRefusal(id);
      setSuccessMsg('Destination reactivated');
      loadDestinations();
    } catch (err) {
      setReactivateRefusals(prev => ({ ...prev, [id]: refusalOf(err, 'Could not reactivate this destination') }));
    }
  };

  const openDelete = async (dest) => {
    setDeleting({ dest, preview: null, error: null, busy: false });
    try {
      const res = await captureAPI.destinations.deletePreview(dest.id);
      if (res?.ok !== true) throw new Error('no preview');
      setDeleting(prev => (prev && prev.dest.id === dest.id
        ? { ...prev, preview: { workflows: Array.isArray(res.workflows) ? res.workflows : [] } }
        : prev));
    } catch (err) {
      setDeleting(prev => (prev && prev.dest.id === dest.id
        ? { ...prev, error: refusalOf(err, 'Could not check which workflows use this destination.') }
        : prev));
    }
  };

  const confirmDelete = async () => {
    if (!deleting?.preview || deleting.busy) return;
    const { dest } = deleting;
    setDeleting(prev => ({ ...prev, busy: true, error: null }));
    try {
      const res = await captureAPI.destinations.remove(dest.id);
      if (!res?.ok) throw new Error(res?.error || 'Delete failed');
      setDeleting(null);
      clearRefusal(dest.id);
      setSuccessMsg(`Destination "${dest.name}" deleted`);
      loadDestinations();
    } catch (err) {
      setDeleting(prev => (prev ? { ...prev, busy: false, error: refusalOf(err, 'Could not delete this destination') } : prev));
    }
  };

  const handleSetDefault = async (id, currentDefault) => {
    try {
      const res = await captureAPI.destinations.update(id, { is_default: !currentDefault });
      if (!res.ok) throw new Error(res.error);
      setSuccessMsg(!currentDefault ? 'Set as default destination' : 'Removed default');
      loadDestinations();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTestConnection = async (id) => {
    setTesting(prev => ({ ...prev, [id]: true }));
    setTestResults(prev => ({ ...prev, [id]: null }));
    try {
      const res = await captureAPI.destinations.test(id);
      setTestResults(prev => ({ ...prev, [id]: res }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [id]: { ok: false, error: err.message } }));
    } finally {
      setTesting(prev => ({ ...prev, [id]: false }));
    }
  };

  const insertFieldToken = (fieldKey, inputRef, configKey) => {
    const token = `{${fieldKey}}`;
    const input = inputRef?.current;
    if (input) {
      const start = input.selectionStart ?? (formData.config[configKey] ?? '').length;
      const end = input.selectionEnd ?? start;
      const current = formData.config[configKey] ?? '';
      const newValue = current.slice(0, start) + token + current.slice(end);
      handleConfigChange(configKey, newValue);
      // Restore cursor after token
      setTimeout(() => {
        input.focus();
        const pos = start + token.length;
        input.setSelectionRange(pos, pos);
      }, 0);
    } else {
      handleConfigChange(configKey, (formData.config[configKey] ?? '') + token);
    }
  };

  const connectorMeta = CONNECTOR_TYPES[formData.connector_type] || {};
  const configFields = connectorMeta.configFields || [];

  return (
    <div className="dest-page">
      <div className="page-header">
        <h1>Publish Destinations</h1>
      </div>

      {/* Cluster 2 / A1 + R1 + A9: destinations are tenant-wide today, so
          create/edit/delete are admin-only (server enforces). Non-admins see a
          muted note here; Test Connection stays available on each card. */}
      <div className="actions-bar">
        {isAdmin ? (
          <button className="btn btn-primary" onClick={openCreateForm}>
            + Add Destination
          </button>
        ) : (
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            Only tenant admins can add or manage publish destinations.
          </span>
        )}
      </div>

      {error && (
        <div className="inline-alert inline-alert-error">
          <span>{error}</span>
          <button className="inline-alert-dismiss" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {successMsg && (
        <div className="inline-alert inline-alert-success">
          <span>{successMsg}</span>
          <button className="inline-alert-dismiss" onClick={() => setSuccessMsg(null)}>×</button>
        </div>
      )}

      {/* Destination cards */}
      <div className="dest-section">
        {loading ? (
          <div className="loading-container">
            <div className="spinner" />
            <p>Loading destinations...</p>
          </div>
        ) : destinations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon" style={{ fontSize: '3rem' }}>📡</div>
            <h3>No Destinations Configured</h3>
            <p>Add a publish destination to start sending captured documents to your document library or other systems.</p>
            {isAdmin && (
              <button className="btn btn-primary" onClick={openCreateForm}>+ Add Destination</button>
            )}
          </div>
        ) : (
          <div className="dest-grid">
            {destinations.map(dest => {
              const meta = CONNECTOR_TYPES[dest.connector_type] || { icon: '🔌', label: dest.connector_type };
              const testResult = testResults[dest.id];
              const isTesting = testing[dest.id];
              return (
                <div key={dest.id} className={`dest-card ${!dest.is_active ? 'dest-card-inactive' : ''}`}>
                  <div className="dest-card-header">
                    <span className="dest-card-icon">{meta.icon}</span>
                    <div className="dest-card-title">
                      <h3>{dest.name}</h3>
                      <span className="dest-type-badge">{meta.label}</span>
                    </div>
                    <div className="dest-card-badges">
                      {dest.is_default && <span className="dest-default-badge">Default</span>}
                      <span className={`dest-status-badge ${dest.is_active ? 'active' : 'inactive'}`}>
                        {dest.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {dest.description && (
                    <p className="dest-card-desc">{dest.description}</p>
                  )}

                  <div className="dest-card-config">
                    {dest.config?.default_folder && (
                      <div className="dest-config-item">
                        <span className="dest-config-label">Folder</span>
                        <span className="dest-config-value">{dest.config.default_folder}</span>
                      </div>
                    )}
                    {dest.connector_type === 'onedrive' && (
                      <>
                        <div className="dest-config-item">
                          <span className="dest-config-label">Folder</span>
                          <span className="dest-config-value">{dest.config?.folder_path || 'No folder chosen'}</span>
                        </div>
                        {dest.config?.subfolder_template && (
                          <div className="dest-config-item">
                            <span className="dest-config-label">Subfolder rule</span>
                            <span className="dest-config-value">{dest.config.subfolder_template}</span>
                          </div>
                        )}
                        {dest.config?.filename_template && (
                          <div className="dest-config-item">
                            <span className="dest-config-label">Filename rule</span>
                            <span className="dest-config-value">{dest.config.filename_template}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Turned off by a Microsoft 365 disconnect, and not since reactivated. */}
                  {!dest.is_active && dest.config?.disconnected_at && (
                    <p className="dest-card-note">
                      Turned off when Microsoft 365 was disconnected. Reactivate it once Microsoft 365 is connected again.
                    </p>
                  )}

                  {/* A refused reactivation: why, and the way to put it right. */}
                  {reactivateRefusals[dest.id] && (
                    <div className="dest-refusal" role="alert" data-code={reactivateRefusals[dest.id].code || undefined}>
                      <span className="dest-refusal-text">{reactivateRefusals[dest.id].error}</span>
                      {reactivateRefusals[dest.id].errorId && (
                        <span className="dest-test-ref">Reference: {reactivateRefusals[dest.id].errorId}</span>
                      )}
                      {isAdmin && dest.connector_type === 'onedrive' && FOLDER_REFUSALS.has(reactivateRefusals[dest.id].code) && (
                        <button
                          className="btn btn-sm btn-secondary dest-refusal-action"
                          onClick={() => setPicker({ mode: 'reactivate', dest })}
                        >
                          Choose folder
                        </button>
                      )}
                      {CONNECTION_REFUSALS.has(reactivateRefusals[dest.id].code) && (
                        <a className="dest-refusal-action" href={SOURCES_PAGE}>Open Document Sources</a>
                      )}
                    </div>
                  )}

                  {/* Test result */}
                  {testResult && (
                    <div className={`dest-test-result ${testResult.ok ? 'success' : 'failure'}`}>
                      {testResult.ok ? '✓ Connection successful' : `✗ ${testResult.error || 'Connection failed'}`}
                      {!testResult.ok && testResult.error_id && (
                        <div className="dest-test-ref">Reference: {testResult.error_id}</div>
                      )}
                    </div>
                  )}

                  <div className="dest-card-actions">
                    {/* Cluster 2 / A1 + R1: test / edit / set-default /
                        deactivate / reactivate are admin-only (server enforces). */}
                    {isAdmin && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleTestConnection(dest.id)}
                        disabled={isTesting || !dest.is_active}
                      >
                        {isTesting ? 'Testing...' : 'Test Connection'}
                      </button>
                    )}
                    {isAdmin && (
                      <button className="btn btn-sm btn-secondary" onClick={() => openEditForm(dest)}>
                        Edit
                      </button>
                    )}
                    {/* Delete is admin-only (server enforces), for an active or
                        an inactive destination alike. */}
                    {isAdmin && (
                      <button className="btn btn-sm btn-danger dest-delete-btn" onClick={() => openDelete(dest)}>
                        Delete
                      </button>
                    )}
                    {isAdmin && (dest.is_active ? (
                      <>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleSetDefault(dest.id, dest.is_default)}
                        >
                          {dest.is_default ? 'Unset Default' : 'Set Default'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeactivate(dest.id)}
                        >
                          Deactivate
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleReactivate(dest.id)}
                      >
                        Reactivate
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="dest-modal-overlay" onClick={closeForm}>
          <div className="dest-modal" onClick={e => e.stopPropagation()}>
            <div className="dest-modal-header">
              <h2>{editingId ? 'Edit Destination' : 'Add Destination'}</h2>
              <button className="dest-modal-close" onClick={closeForm}>×</button>
            </div>

            <div className="dest-modal-body">
              {formError && (
                <div className="inline-alert inline-alert-error dest-form-error" role="alert" data-code={formError.code || undefined} style={{ margin: '0 0 16px 0', padding: '10px 14px' }}>
                  <span>{formError.error}</span>
                  {formError.errorId && <span className="dest-test-ref"> Reference: {formError.errorId}</span>}
                  {CONNECTION_REFUSALS.has(formError.code) && (
                    <> <a href={SOURCES_PAGE}>Open Document Sources</a></>
                  )}
                </div>
              )}

              <div className="dest-form-group">
                <label>Name *</label>
                <input
                  type="text"
                  className="dest-form-input"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Production Library"
                />
              </div>

              <div className="dest-form-group">
                <label>Connector Type</label>
                <select
                  className="dest-form-input"
                  value={formData.connector_type}
                  onChange={e => handleTypeChange(e.target.value)}
                  disabled={!!editingId}
                >
                  {/* A new destination has no type until one is chosen. The
                      placeholder cannot be chosen back, and is not offered
                      when editing (an existing destination has its type). */}
                  {!editingId && <option value="" disabled>Choose a type...</option>}
                  {Object.entries(CONNECTOR_TYPES).map(([key, meta]) => (
                    // A type that needs Microsoft 365 is offered only when it is
                    // connected. (An existing destination of the type can still
                    // be opened: its type is shown, and cannot be changed.)
                    <option key={key} value={key} disabled={!editingId && !typeAvailable(key)}>
                      {meta.icon} {meta.label}{!editingId && !typeAvailable(key) ? ' (connect Microsoft 365 first)' : ''}
                    </option>
                  ))}
                </select>
                {!editingId && connection !== undefined && !m365Usable && (
                  <span className="dest-form-helper dest-connect-note">
                    {connection
                      ? 'OneDrive is unavailable: Microsoft 365 needs to be reconnected. '
                      : 'OneDrive is unavailable: Microsoft 365 is not connected. '}
                    Connect it under <a href={SOURCES_PAGE}>Settings, Document Sources</a>, then add a OneDrive destination here.
                  </span>
                )}
              </div>

              <div className="dest-form-group">
                <label>Description</label>
                <input
                  type="text"
                  className="dest-form-input"
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>

              <div className="dest-form-divider" />

              <h3 className="dest-form-section-title">Configuration</h3>

              {configFields.map(field => {
                // Determine which ref to use for field chip insertion
                const inputRef = field.key === 'filename_template' ? filenameInputRef
                  : field.key === 'subfolder_template' ? subfolderInputRef
                  : undefined;

                return (
                  <div className="dest-form-group" key={field.key}>
                    <label>{field.label}{field.required ? ' *' : ''}</label>
                    {field.type === 'onedrive_folder' ? (
                      // Chosen in the picker, never typed: the form holds the
                      // folder's id and drive, and shows its path.
                      <div className="dest-folder-choice">
                        <span className={`dest-folder-path ${formData.config.folder_item_id ? '' : 'dest-folder-path-empty'}`}>
                          {formData.config.folder_item_id ? (formData.config.folder_path || 'Folder chosen') : 'No folder chosen'}
                        </span>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => setPicker({ mode: 'form' })}>
                          {formData.config.folder_item_id ? 'Change folder' : 'Choose folder'}
                        </button>
                      </div>
                    ) : field.type === 'select' ? (
                      <select
                        className="dest-form-input"
                        value={formData.config[field.key] ?? field.defaultValue ?? ''}
                        onChange={e => handleConfigChange(field.key, e.target.value)}
                      >
                        {field.options.map(opt => {
                          // Support both string options and {value, label} objects
                          const val = typeof opt === 'object' ? opt.value : opt;
                          const lbl = typeof opt === 'object' ? opt.label : opt;
                          return <option key={val} value={val}>{lbl}</option>;
                        })}
                      </select>
                    ) : (
                      <input
                        ref={inputRef}
                        type="text"
                        className="dest-form-input"
                        value={formData.config[field.key] ?? ''}
                        onChange={e => handleConfigChange(field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                      />
                    )}
                    {field.helperText && (
                      <span className="dest-form-helper">{field.helperText}</span>
                    )}
                    {/* Field key chips for template fields */}
                    {field.showFieldChips && fieldChips.length > 0 && (
                      <div className="dest-field-chips">
                        <span className="dest-field-chips-label">Insert field:</span>
                        {fieldChips.map(fk => (
                          <button
                            key={fk}
                            type="button"
                            className="dest-field-chip"
                            onClick={() => insertFieldToken(fk, inputRef, field.key)}
                          >
                            {fk}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Vendor record tokens, and what a document without the value gets */}
                    {field.showFieldChips && vendorChips.length > 0 && (
                      <div className="dest-field-chips dest-vendor-chips">
                        <span className="dest-field-chips-label">From the vendor record:</span>
                        {vendorChips.map(fk => (
                          <button
                            key={fk}
                            type="button"
                            className="dest-field-chip"
                            onClick={() => insertFieldToken(fk, inputRef, field.key)}
                          >
                            {fk}
                          </button>
                        ))}
                      </div>
                    )}
                    {field.showFieldChips && vendorChips.length > 0 && VENDOR_NOTES[field.key]
                      && usesVendorToken(formData.config[field.key]) && (
                      <span className="dest-form-helper dest-vendor-note" role="note">{VENDOR_NOTES[field.key]}</span>
                    )}
                    {/* Live filename preview for filename_template */}
                    {field.key === 'filename_template' && formData.config.filename_template && (
                      <div className="dest-filename-preview">
                        <span className="dest-filename-preview-label">Preview:</span>{' '}
                        <code>{resolveTokens(formData.config.filename_template, PREVIEW_SAMPLE)}.pdf</code>
                        {formData.config.sidecar_format && formData.config.sidecar_format !== 'none' && (
                          <> + <code>{resolveTokens(formData.config.filename_template, PREVIEW_SAMPLE)}.{formData.config.sidecar_format}</code></>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="dest-form-group dest-form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={e => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                  />
                  Set as default destination
                </label>
              </div>

              {/* Destination-level mapping is for file/folder exports only (file
                  naming tokens and the data file). A Library destination's
                  renames live in each workflow's Publish node. */}
              {editingId && formData.connector_type !== 'document_library' && (
                <>
                  <div className="dest-form-divider" />
                  <FieldMappingEditor
                    destinationId={editingId}
                    currentMapping={formData.config?.field_mapping || {}}
                    connectorType={formData.connector_type}
                    onMappingChange={(newMapping) => {
                      setFormData(prev => ({
                        ...prev,
                        config: { ...prev.config, field_mapping: newMapping },
                      }));
                    }}
                  />
                </>
              )}
            </div>

            <div className="dest-modal-footer">
              <button className="btn btn-secondary" onClick={closeForm} disabled={saving}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || (!editingId && !formData.connector_type)}
                title={!editingId && !formData.connector_type ? 'Choose a type first' : undefined}
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation. It says which workflows publish to the
          destination BEFORE anything changes; with none, it says so. */}
      {deleting && (
        <div className="dest-modal-overlay" onClick={() => !deleting.busy && setDeleting(null)}>
          <div className="dest-modal dest-delete-dialog" role="dialog" aria-label="Delete destination" onClick={e => e.stopPropagation()}>
            <div className="dest-modal-header">
              <h2>Delete &ldquo;{deleting.dest.name}&rdquo;?</h2>
              <button className="dest-modal-close" onClick={() => setDeleting(null)} disabled={deleting.busy}>×</button>
            </div>
            <div className="dest-modal-body">
              {!deleting.preview && !deleting.error && (
                <p className="dest-delete-loading">Checking which workflows use this destination&hellip;</p>
              )}
              {deleting.preview && (
                <>
                  {deleting.preview.workflows.length > 0 ? (
                    <>
                      <p>
                        {deleting.preview.workflows.length === 1
                          ? 'This workflow publishes to it:'
                          : 'These workflows publish to it:'}
                      </p>
                      <ul className="dest-delete-workflows">
                        {deleting.preview.workflows.map(wf => (
                          <li key={wf.id}>
                            <strong>{wf.name}</strong>
                            <span className="dest-delete-workflow-status"> ({wf.status})</span>
                          </li>
                        ))}
                      </ul>
                      <p className="dest-delete-warning">
                        {deleting.preview.workflows.length === 1
                          ? 'Publishing in that workflow will fail until a new destination is chosen in it.'
                          : 'Publishing in those workflows will fail until a new destination is chosen in each.'}
                      </p>
                    </>
                  ) : (
                    <p>No workflow publishes to this destination.</p>
                  )}
                  <p>
                    The destination is removed from this list and cannot be brought back. The record of what was
                    already published to it is kept, and nothing is deleted from where the documents went.
                  </p>
                </>
              )}
              {deleting.error && (
                <div className="inline-alert inline-alert-error dest-delete-error" role="alert" data-code={deleting.error.code || undefined}>
                  <span>{deleting.error.error}</span>
                  {deleting.error.errorId && <span className="dest-test-ref"> Reference: {deleting.error.errorId}</span>}
                </div>
              )}
            </div>
            <div className="dest-modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleting(null)} disabled={deleting.busy}>Cancel</button>
              <button className="btn btn-danger dest-delete-confirm" onClick={confirmDelete} disabled={deleting.busy || !deleting.preview}>
                {deleting.busy ? 'Deleting...' : 'Delete destination'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The OneDrive folder picker. For the form it only fills the folder in;
          from a refused reactivation it replaces the folder and reactivates in
          one request. The drive's top level cannot be chosen (the dialog's
          default), and the server refuses it as well. */}
      {picker && isAdmin && (
        <CloudFolderPickerDialog
          source={connectionSource}
          title="Choose a OneDrive folder"
          currentLabel="Currently"
          currentPath={picker.mode === 'form' ? formData.config.folder_path : picker.dest.config?.folder_path}
          onClose={() => setPicker(null)}
          onSelected={(picked) => {
            if (picker.mode === 'form') {
              setFormData(prev => ({ ...prev, config: { ...prev.config, ...picked } }));
              setFormError(null);
            } else {
              handleReactivate(picker.dest.id, { ...picker.dest.config, ...picked });
            }
          }}
        />
      )}
    </div>
  );
};

export default DestinationsPage;
